import { LogManager } from '@src/utility/Logger';

import { AbstractCommand } from '../AbstractCommand';

import type { CaptionLineBreak } from '../../models/karaoke/CaptionLineBreak';
import type { VideoEditor } from '../../VideoEditor';

const logger = LogManager.getLogger('karaoke:syntheticBreak');

/**
 * PLAT-9623: drop EVERY synthetic line-break record on the karaoke
 * track. Used by the captions panel's "Reset captions" gear action so
 * a follow-up `commandProduceKaraoke` starts from the producer's
 * default per-phrase segmentation, not the post-split state.
 *
 * Reversible. The clip array itself is not touched here — Reset is a
 * two-step operation (clear records, then re-produce); the produce
 * command rebuilds clips from translation phrases.
 */
export class ClearSyntheticCaptionBreaksCommand extends AbstractCommand {
  private prevBreaks: CaptionLineBreak[] | null = null;

  constructor(editor: VideoEditor) {
    super(editor);
  }

  getName(): string {
    return 'ClearSyntheticCaptionBreaksCommand';
  }

  getDescription(): string {
    return 'Clear all synthetic caption line breaks';
  }

  execute(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track) {
      logger.debug('execute: no karaoke track');
      return false;
    }
    if (track.syntheticBreaks.length === 0) {
      // No-op — nothing to undo, no dirty mark needed.
      return false;
    }
    this.prevBreaks = [...track.syntheticBreaks];
    track.syntheticBreaks = [];
    this.editor.markDirty('synthetic_caption_breaks_cleared');
    return true;
  }

  undo(): boolean {
    if (!this.prevBreaks) return false;
    const track = this.editor.getKaraokeTrack();
    if (!track) return false;
    track.syntheticBreaks = this.prevBreaks;
    this.editor.markDirty('synthetic_caption_breaks_cleared_undo');
    return true;
  }
}
